import React from 'react';
import {
    Box,
    Button,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, Delete } from '@mui/icons-material';

import { Header, Popup } from '@commonComponents';
import ShapeAreaEditor from '@commonComponents/GoogleMap/ShapeAreaEditor';
import type { AreaShape } from '@commonComponents/GoogleMap/ShapeAreaEditor';
import type { EditableRouteMapHandle } from '@commonComponents/GoogleMap/EditableRouteMap';
import { Container } from '@styledComponents';
import {
    createTenantRoute,
    deleteTenantRoute,
    getTenantRoutes,
    updateTenantRoute
} from '@api/tenantRoutes';
import { getTenantProfile } from '@api/tenants';
import toast from '@utils/toast';
import type { TenantRoute, TenantRoutePoint } from '@types';
import {
    MAX_TENANT_ROUTES,
    HOME_ROUTE_RADIUS_M,
    circleRoutePoints,
    nextRouteName,
    polygonToCircle,
    routeColor,
    squareRoutePoints
} from '@utils/tenantRoutes';

const isNew = (route: TenantRoute) => route.id < 0;

// A circle's points are derived from it, so only the shape counts for circles.
const isDirty = (route: TenantRoute, original?: TenantRoute) => (
    !original
    || route.name !== original.name
    || (route.isPublic ?? true) !== (original.isPublic ?? true)
    || JSON.stringify(route.shape ?? null) !== JSON.stringify(original.shape ?? null)
    || (!route.shape && JSON.stringify(route.points) !== JSON.stringify(original.points))
);

const TenantRoutes: React.FC = () => {
    const [routes, setRoutes] = React.useState<TenantRoute[]>([]);
    const [originals, setOriginals] = React.useState<TenantRoute[]>([]);
    const [selectedId, setSelectedId] = React.useState<number | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [savingId, setSavingId] = React.useState<number | null>(null);
    const [routeToDelete, setRouteToDelete] = React.useState<TenantRoute | null>(null);
    const [deleting, setDeleting] = React.useState(false);
    const [home, setHome] = React.useState<{ lat?: number | null, lng?: number | null }>({});
    const mapRef = React.useRef<EditableRouteMapHandle>(null);

    // Fetches the saved routes, keeping unsaved local edits except for `resetIds`
    const load = React.useCallback(async (resetIds: number[] = [], dropIds: number[] = []) => {
        const fresh = await getTenantRoutes();
        setOriginals(fresh);
        setRoutes((current) => {
            const merged = fresh.map((serverRoute) => {
                const local = current.find((route) => route.id === serverRoute.id);
                return local && !resetIds.includes(serverRoute.id)
                    ? { ...local, watchlistCount: serverRoute.watchlistCount }
                    : serverRoute;
            });
            const unsaved = current.filter((route) => isNew(route) && !dropIds.includes(route.id));
            return [...merged, ...unsaved];
        });
        return fresh;
    }, []);

    React.useEffect(() => {
        (async () => {
            try {
                const [fresh, profile] = await Promise.all([load(), getTenantProfile()]);
                setHome({ lat: profile?.lat, lng: profile?.lng });
                setSelectedId((current) => current ?? fresh[0]?.id ?? null);
            } catch (e) {
                toast(e, 'Kunde inte hämta rutter, försök igen');
            } finally {
                setLoading(false);
            }
        })();
    }, [load]);

    const select = (id: number) => {
        mapRef.current?.commitSelected();
        setSelectedId(id);
    };

    const change = (id: number, changes: Partial<TenantRoute>) => {
        setRoutes((current) => current.map((route) => (
            route.id === id ? { ...route, ...changes } : route
        )));
    };

    const addRoute = () => {
        if (routes.length >= MAX_TENANT_ROUTES) return;
        mapRef.current?.commitSelected();
        const id = -Date.now();
        setRoutes((current) => [...current, {
            id,
            name: nextRouteName(current),
            points: squareRoutePoints(home.lat, home.lng),
            shape: null,
            isPublic: true,
            watchlistCount: 0
        }]);
        setSelectedId(id);
    };

    const selectedRoute = routes.find((route) => route.id === selectedId);
    const selectedCircle = selectedRoute?.shape ?? null;

    // A circle is saved as its shape; `points` holds the matching polygon for the map
    const changeCircle = (center: TenantRoutePoint, radiusM: number) => {
        if (!selectedRoute) return;
        change(selectedRoute.id, {
            shape: { type: 'circle', center, radius: radiusM },
            points: circleRoutePoints(center.lat, center.lng, radiusM, 32)
        });
    };

    const changeShape = (shape: AreaShape) => {
        if (!selectedRoute || (shape === 'circle') === !!selectedCircle) return;
        if (shape === 'circle') {
            const { center, radiusM } = polygonToCircle(selectedRoute.points);
            changeCircle(center, radiusM);
        } else {
            // The polygon starts from the circle's points
            change(selectedRoute.id, { shape: null });
        }
    };

    const changePoints = (id: number, points: TenantRoutePoint[]) => change(id, { points });

    const save = async (route: TenantRoute) => {
        const name = route.name.trim();
        if (!name) {
            toast('Rutten måste ha ett namn');
            return;
        }
        setSavingId(route.id);
        try {
            const body = route.shape
                ? { name, shape: route.shape, isPublic: route.isPublic ?? true }
                : {
                    name, points: route.points, shape: null, isPublic: route.isPublic ?? true
                };
            if (isNew(route)) {
                const created = await createTenantRoute(body);
                await load([], [route.id]);
                if (selectedId === route.id) setSelectedId(created?.id ?? null);
                toast('Rutten är skapad. Du har fått en aktiv bevakning på den.');
            } else {
                await updateTenantRoute(route.id, body);
                await load([route.id]);
                toast('Rutten är sparad');
            }
        } catch (e) {
            toast(e, 'Kunde inte spara rutten, försök igen');
        } finally {
            setSavingId(null);
        }
    };

    const confirmDelete = async () => {
        if (!routeToDelete) return;
        setDeleting(true);
        try {
            if (!isNew(routeToDelete)) {
                await deleteTenantRoute(routeToDelete.id);
            }
            const remaining = routes.filter((route) => route.id !== routeToDelete.id);
            setRoutes(remaining);
            if (selectedId === routeToDelete.id) setSelectedId(remaining[0]?.id ?? null);
            if (!isNew(routeToDelete)) {
                await load();
                toast('Rutten är borttagen');
            }
            setRouteToDelete(null);
        } catch (e) {
            toast(e, 'Kunde inte ta bort rutten, försök igen');
        } finally {
            setDeleting(false);
        }
    };

    const affected = routeToDelete && !isNew(routeToDelete) ? routeToDelete.watchlistCount ?? 0 : 0;

    return (
        <Container>
            <Header
                title="Rutter"
                subtitle="Företagets rutter används som områden i era bevakningar."
                CustomComponent={(
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={addRoute}
                        disabled={loading || routes.length >= MAX_TENANT_ROUTES}
                    >
                        Lägg till rutt
                    </Button>
                )}
            />
            {!loading && routes.length === 0 && (
                <Typography sx={{ mb: 2 }}>
                    Ni har inga rutter ännu. Lägg till en rutt för att kunna bevaka uppdrag längs den.
                </Typography>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                    {routes.map((route, index) => {
                        const selected = route.id === selectedId;
                        const dirty = isDirty(route, originals.find((o) => o.id === route.id));
                        return (
                            <Box
                                key={route.id}
                                onClick={() => select(route.id)}
                                sx={{
                                    mb: 1,
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    bgcolor: selected ? 'action.hover' : 'transparent',
                                    cursor: 'pointer'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: routeColor(index),
                                            flexShrink: 0
                                        }}
                                    />
                                    <TextField
                                        label="Namn"
                                        size="small"
                                        fullWidth
                                        value={route.name}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => change(route.id, { name: e.target.value })}
                                        inputProps={{ maxLength: 80 }}
                                        required
                                    />
                                    <IconButton
                                        color="error"
                                        size="small"
                                        aria-label="Ta bort rutt"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setRouteToDelete(route);
                                        }}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mt: 1
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <FormControlLabel
                                        control={(
                                            <Switch
                                                size="small"
                                                checked={route.isPublic ?? true}
                                                onChange={(e) => change(route.id, { isPublic: e.target.checked })}
                                            />
                                        )}
                                        label="Visa på profilsidan"
                                    />
                                    <Typography variant="caption">
                                        {isNew(route)
                                            ? 'Inte sparad'
                                            : `${route.watchlistCount ?? 0} bevakningar`}
                                    </Typography>
                                    <LoadingButton
                                        variant="contained"
                                        size="small"
                                        disabled={!dirty || savingId !== null}
                                        loading={savingId === route.id}
                                        onClick={() => save(route)}
                                    >
                                        Spara
                                    </LoadingButton>
                                </Box>
                            </Box>
                        );
                    })}
                </Grid>
                <Grid item xs={12} md={7} sx={{ position: 'relative', isolation: 'isolate' }}>
                    <ShapeAreaEditor
                        ref={mapRef}
                        shape={selectedCircle ? 'circle' : 'polygon'}
                        onShapeChange={changeShape}
                        showToggle={!!selectedRoute}
                        routes={routes}
                        selectedId={selectedId}
                        fitToId={selectedId}
                        onSelect={select}
                        onPointsChange={changePoints}
                        circleCenter={selectedCircle?.center ?? null}
                        circleRadiusM={selectedCircle?.radius ?? HOME_ROUTE_RADIUS_M}
                        onCircleChange={changeCircle}
                        otherRoutes={routes
                            .map((route, index) => ({ ...route, color: routeColor(index) }))
                            .filter((route) => route.id !== selectedId)}
                        height="clamp(250px, 55vh, 520px)"
                    />
                </Grid>
            </Grid>
            <Popup
                open={!!routeToDelete}
                title="Ta bort rutt"
                deleteLabel="Ta bort"
                handleDelete={confirmDelete}
                handleClose={() => setRouteToDelete(null)}
                isSubmitting={deleting}
            >
                <Typography>
                    {`Vill du verkligen ta bort rutten "${routeToDelete?.name || ''}"?`}
                </Typography>
                {affected > 0 && (
                    <Typography sx={{ mt: 1 }}>
                        {`${affected} ${affected === 1 ? 'bevakning påverkas' : 'bevakningar påverkas'}. `
                            + 'De pausas och kopplas loss från rutten, och användarna får en notis.'}
                    </Typography>
                )}
            </Popup>
        </Container>
    );
};

export default TenantRoutes;
